/**
 * API 服务模块
 * 提供 HTTP 请求封装、认证拦截、错误处理等功能
 */
import axios from 'axios';
import { getAuthToken, clearAuth } from './auth';

// API 基础地址
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/wifi';

// 创建 axios 实例
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' },
});

// 请求拦截器 - 自动添加 Token
apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 响应拦截器 - 处理 401 错误
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      clearAuth();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * 统一处理 API 响应
 * @param {Promise} request - axios 请求
 * @returns {Promise} 业务数据
 */
const handleResponse = async (request) => {
  const response = await request;
  if (response.data.code === 0) {
    return response.data.data;
  }
  throw new Error(response.data.message || '请求失败');
};

// ============ 设备相关 API ============

/**
 * 获取设备列表
 */
export const getDevices = async (deviceIDs = null) => {
  const body = deviceIDs?.length > 0 ? { device_ids: deviceIDs } : {};
  return (await handleResponse(apiClient.post('/devices', body))) || [];
};

/**
 * 获取设备详情（含流量统计）
 */
export const getDeviceDetail = async (mac, deviceIDs = null) => {
  const body = { mac, device_ids: deviceIDs?.length > 0 ? deviceIDs : undefined };
  const device = await handleResponse(apiClient.post('/devices/detail', body));
  
  if (device?.device_id) {
    try {
      const stats = await handleResponse(
        apiClient.post('/traffic/stats', { mac, device_id: device.device_id })
      );
      return { ...device, ...stats };
    } catch (e) {
      console.error('获取流量统计失败:', e);
    }
  }
  return device;
};

export default apiClient;
